/**
 * Página pública para compartir un logro individual de ProPoints
 * 
 * URL: /share/partido/:partidoId/futbolista/:futbolistaId/logro/:logroNombre
 * 
 * Muestra un logro específico obtenido por un futbolista en un partido
 * con diseño optimizado para compartir en redes sociales
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../../styles/share-logros.css';
import { formatFechaPartido } from '../../utils/fechaUtils';

const ShareLogroIndividual = () => {
  const { partidoId, futbolistaId, logroNombre } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogro = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/postgres/share/partido/${partidoId}/futbolista/${futbolistaId}/logro/${logroNombre}`
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setData(result.data);
          } else {
            setError(result.message);
          }
        } else {
          setError('No se pudo cargar el logro');
        }
      } catch (err) {
        console.error('Error cargando logro:', err);
        setError('Error al cargar el logro');
      } finally {
        setLoading(false);
      }
    };

    fetchLogro();
  }, [partidoId, futbolistaId, logroNombre]);

  // Helper: Normalizar nombre de logro de BD a identificador técnico URL-friendly
  const normalizeLogroNombre = (logroNombre) => {
    // Simplemente convertir a minúsculas y reemplazar espacios por guiones bajos
    // Esto mantiene la consistencia con los nombres de la BD
    return logroNombre
      .toLowerCase()
      .normalize('NFD') // Normalizar caracteres Unicode
      .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
      .replace(/\s+/g, '_'); // Reemplazar espacios por guiones bajos
  };

  const getLogroImageUrl = (logroNombre, genero) => {
    const baseUrl = window.location.origin;
    const isFemenino = genero === 'F';

    // Normalizar el nombre del logro primero
    const normalizedNombre = normalizeLogroNombre(logroNombre);

    const imageMapMasculino = {
      'participacion': '/images/jugadorcampo-1x.jpeg',
      'victoria': '/images/jugadorcampo-1x.jpeg',
      'destacado': '/images/jugadorcampo-1x.jpeg',
      'el_jugon': '/images/eljugon-1x.jpeg',
      'el_cerrojo': '/images/cerrojo-1x.jpeg',
      'el_desequilibrante': '/images/desequilibrante-1x.jpeg',
      'el_lider': '/images/lider-1x.jpeg',
      'el_mejor_companero': '/images/mejorcompanero-1x.jpeg',
      'mayor_progresion': '/images/progresion-1x.jpeg',
      'constancia': '/images/constante-1x.jpeg',
      'ganador_incansable': '/images/incansable-1x.jpeg',
      'portero': '/images/portero-1x.jpeg'
    };

    const imageMapFemenino = {
      'participacion': '/images/la_jugadorcampo.png',
      'victoria': '/images/la_jugadorcampo.png',
      'destacado': '/images/la_jugadorcampo.png',
      'el_jugon': '/images/la_eljugon.png',
      'el_cerrojo': '/images/la_cerrojo.png',
      'el_desequilibrante': '/images/la_desequilibrante.png',
      'el_lider': '/images/la_lider.png',
      'el_mejor_companero': '/images/la_mejorcompanero.png',
      'mayor_progresion': '/images/la_progresion.png',
      'constancia': '/images/la_constante.png',
      'ganador_incansable': '/images/la_incansable.png',
      'portero': '/images/la_portero.png'
    };

    const imageMap = isFemenino ? imageMapFemenino : imageMapMasculino;
    const defaultImage = isFemenino ? '/images/la_jugadorcampo.png' : '/images/jugadorcampo-1x.jpeg';

    const imagePath = imageMap[normalizedNombre] || defaultImage;
    return `${baseUrl}${imagePath}`;
  };

  const getLogroDisplayName = (logroNombre) => {
    const displayNames = {
      'asistencia': 'Asistencia',
      'victoria': 'Victoria',
      'MVP': 'MVP',
      'destacado': 'Destacado',
      'especial': 'Especial',
      'el_jugon': 'El Jugón',
      'el_cerrojo': 'El Cerrojo',
      'el_machete': 'El Machete',
      'el_lider': 'El Líder',
      'el_mejor_companero': 'El Mejor Compañero',
      'mayor_progresion': 'Mayor Progresión',
      'constancia': 'Constancia',
      'ganador_incansable': 'Ganador Incansable',
      'portero': 'Portero'
    };
    return displayNames[logroNombre] || logroNombre;
  };

  if (loading) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-loading">
          <div className="spinner"></div>
          <p>Cargando logro...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-error">
          <h2>❌ Error</h2>
          <p>{error || 'No se encontró el logro'}</p>
        </div>
      </div>
    );
  }

  const { futbolista, partido, logro } = data;
  const logroImageUrl = getLogroImageUrl(logro.logro_nombre, futbolista.genero);
  const logroDisplayName = getLogroDisplayName(logro.logro_nombre);
  const shareUrl = data.share_url || window.location.href;
  const pageTitle = `${futbolista.nombre} - ${logroDisplayName} | Liga ProKids`;
  const pageDescription = `¡${futbolista.nombre} ha conseguido el logro "${logroDisplayName}" (${logro.logro_puntos} ProPoints) en la Jornada ${partido.jornada_numero}!`;

  // Detectar si es móvil
  // eslint-disable-next-line no-unused-vars
  const isMobile = window.innerWidth <= 768;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={logroImageUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={shareUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={logroImageUrl} />
      </Helmet>

      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '12px' : '20px'
      }}>
        <div style={{
          maxWidth: isMobile ? '100%' : '500px',
          width: '100%',
          background: 'white',
          borderRadius: isMobile ? '20px' : '24px',
          padding: isMobile ? '24px 20px' : '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          {/* Información del Futbolista */}
          <h1 style={{
            fontSize: isMobile ? '1.6rem' : '2rem',
            color: '#061156',
            marginBottom: '6px',
            fontWeight: '700',
            lineHeight: '1.2'
          }}>
            {futbolista.nombre}
          </h1>
          <p style={{
            color: '#666',
            fontSize: isMobile ? '0.9rem' : '1rem',
            marginBottom: isMobile ? '20px' : '24px'
          }}>
            {futbolista.equipo}
          </p>

          {/* Carta del Logro - OPTIMIZADA PARA MÓVIL */}
          <div style={{
            margin: isMobile ? '20px auto' : '30px auto',
            maxWidth: isMobile ? '280px' : '320px'
          }}>
            <img
              src={logroImageUrl}
              alt={logroDisplayName}
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: isMobile ? '12px' : '16px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                border: isMobile ? '3px solid #4352A9' : '4px solid #4352A9'
              }}
            />
          </div>

          {/* Detalles del Logro */}
          <div style={{
            marginTop: isMobile ? '20px' : '24px',
            padding: isMobile ? '16px' : '20px',
            background: '#F8F9FF',
            borderRadius: isMobile ? '12px' : '16px',
            border: '2px solid #E8ECFF'
          }}>
            <h3 style={{
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              color: '#061156',
              marginBottom: '8px',
              fontWeight: '600',
              lineHeight: '1.3'
            }}>
              {logroDisplayName}
            </h3>
            <p style={{
              color: '#666',
              fontSize: isMobile ? '0.9rem' : '1rem',
              marginBottom: '12px',
              lineHeight: '1.4'
            }}>
              {logro.logro_descripcion}
            </p>
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #4352A9 0%, #061156 100%)',
              color: 'white',
              padding: isMobile ? '10px 20px' : '12px 24px',
              borderRadius: isMobile ? '10px' : '12px',
              fontSize: isMobile ? '1.1rem' : '1.2rem',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(67, 82, 169, 0.3)'
            }}>
              {logro.logro_puntos} ProPoints
            </div>
            {logro.comentario && (
              <p style={{
                marginTop: isMobile ? '12px' : '16px',
                color: '#4352A9',
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                fontStyle: 'italic',
                padding: isMobile ? '10px' : '12px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #E8ECFF',
                lineHeight: '1.4'
              }}>
                💬 {logro.comentario}
              </p>
            )}
          </div>

          {/* Información del Partido */}
          <div style={{
            marginTop: isMobile ? '20px' : '24px',
            padding: isMobile ? '12px' : '16px',
            background: '#FFF8E1',
            borderRadius: isMobile ? '10px' : '12px',
            border: '2px solid #FFE082'
          }}>
            <p style={{
              margin: '4px 0',
              color: '#666',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              lineHeight: '1.4'
            }}>
              <strong>Jornada {partido.jornada_numero}</strong>
            </p>
            <p style={{
              margin: '4px 0',
              color: '#666',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              lineHeight: '1.4'
            }}>
              {partido.sede} - {partido.torneo}
            </p>
            <p style={{
              margin: '4px 0',
              color: '#999',
              fontSize: isMobile ? '0.75rem' : '0.85rem',
              lineHeight: '1.4'
            }}>
              {formatFechaPartido(partido.fecha_partido, partido.jornada_fecha)}
            </p>
          </div>

          {/* Call to Action */}
          <div style={{ marginTop: isMobile ? '20px' : '24px' }}>
            <a
              href="https://ligaprokids.com"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: isMobile ? '12px 28px' : '14px 32px',
                borderRadius: isMobile ? '10px' : '12px',
                textDecoration: 'none',
                fontSize: isMobile ? '0.95rem' : '1rem',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                transition: 'transform 0.2s',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Únete a Liga ProKids
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareLogroIndividual;

